<template>
  <DiscardChangesModal
    :open="showDiscardChangesModal"
    :modal_config="props.modal_config"
    @close-modal="showDiscardChangesModal = false"
    @confirm="emit('close-modal')"
  />
  <Modal :open="props.open" :hideClose="true">
    <template #modal-header>
      <div>
        <div class="flex items-center">
          <Icon name="Pencil" class="w-4 h-4 mr-1 stroke-info" />
          <div class="text-sm font-inter font-semibold text-info">Editing</div>
        </div>
        <div class="font-league tracking-[-.56px] font-semibold text-[1.75rem] text-strongest">
          Investor Questionnaire
        </div>
      </div>
    </template>

    <template #modal-body>
      <div class="p-2 lg:p-6">
        <div>
          <TabView
            :tabs="props.modal_config.tabs"
            :activeTab="activeProfileTab"
            @update:activeTab="(key) => (activeProfileTab = key)"
          >
            <template
              v-for="subtab in props.modal_config.tabs"
              :key="subtab.slot_name"
              #[subtab.slot_name]
            >
              <div v-if="subtab.label === 'Income Information'">
                <Form
                  :schema_array="
                    props.modal_config.schema.filter(
                      (item) => item.record_detail_group === subtab.label
                    )
                  "
                  :state_object="props.modal_config.object_to_edit"
                  :record_id="null"
                  variant="secondary"
                />
              </div>
              <div v-if="subtab.label === 'Questionnaire'" class="flex flex-col gap-6">
                <div
                  v-for="(item, index) in props.modal_config.schema.filter(
                    (item) => item.record_detail_group === 'Questionnaire'
                  )"
                  :key="index"
                  class="py-4 px-3 bg-container-bg rounded-xl"
                >
                  <div class="text-lg font-inter font-semibold text-strongest mb-4">
                    {{ index + 1 }}. {{ item.label }}
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div
                      v-for="option in item.dropdown_options"
                      :key="option.value"
                      class="py-3 px-2 flex items-center rounded-xl w-full"
                      :class="
                        props.modal_config.object_to_edit[item.field_name] == option
                          ? 'bg-radio-checkbox-outline'
                          : 'bg-container-bg'
                      "
                      @click="props.modal_config.object_to_edit[item.field_name] = option"
                    >
                      <div class="flex items-center">
                        <div class="mr-3 flex items-center">
                          <!-- Checked -->
                          <div
                            v-if="props.modal_config.object_to_edit[item.field_name] === option"
                            class="w-4 h-4 rounded-full bg-radio-checkbox-checked flex items-center justify-center"
                          >
                            <div class="w-2 h-2 rounded-full bg-radio-checkbox-outline"></div>
                          </div>

                          <!-- Unchecked -->
                          <div
                            v-else
                            class="w-4 h-4 rounded-full border border-radio-checkbox-outline"
                          ></div>
                        </div>

                        <div
                          class="font-inter text-base text-strongest"
                          v-if="item.field_name !== 'investment_objective'"
                          :class="
                            props.modal_config.object_to_edit[item.field_name] == option
                              ? 'font-semibold'
                              : ''
                          "
                        >
                          {{ option }}
                        </div>
                        <div v-else>
                          <div
                            class="mb-1 font-inter text-base text-strongest"
                            :class="
                              props.modal_config.object_to_edit[item.field_name] == option
                                ? 'font-semibold'
                                : ''
                            "
                          >
                            {{ option.split("-")[0] }}
                          </div>
                          <div
                            class="font-inter text-sm"
                            :class="
                              props.modal_config.object_to_edit[item.field_name] == option
                                ? 'text-stronger'
                                : 'text-strong'
                            "
                          >
                            {{ capitalize(option.split("-")[1]) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="mt-3 ml-3 font-inter text-error text-sm flex items-center"
                    v-if="
                      schemaStore.show_input_errors &&
                      validationErrors.field_errors[item.field_name] &&
                      !props.modal_config.object_to_edit[item.field_name]
                    "
                  >
                    <Icon name="Asterisk" class="mr-2 h-[14px] w-[14px] -skew-x-12 stroke-error" />
                    Please select a value
                  </div>

                  <!-- Conditional fields when employment_status is "Employed" or "Self-Employed" -->
                  <div
                    v-if="
                      item.field_name === 'employment_status' &&
                      (props.modal_config.object_to_edit.employment_status === 'Employed' ||
                        props.modal_config.object_to_edit.employment_status === 'Self-Employed')
                    "
                    class="mt-4 flex flex-col md:flex-row gap-3"
                  >
                    <div class="flex-1">
                      <BaseInput
                        label="Occupation"
                        field_name="occupation"
                        field_type="string"
                        v-model="dataStore.contact.occupation"
                        :error_message="showEmploymentErrors && !dataStore.contact.occupation?.trim() ? 'This field is required.' : ''"
                      />
                    </div>
                    <div class="flex-1">
                      <BaseInput
                        label="Employer"
                        field_name="employer"
                        field_type="string"
                        v-model="dataStore.contact.employer"
                        :error_message="showEmploymentErrors && !dataStore.contact.employer?.trim() ? 'This field is required.' : ''"
                      />
                    </div>
                  </div>

                  <!-- Conditional fields when employed_by_security_or_financial_regulatory is "Yes" -->
                  <div
                    v-if="
                      item.field_name === 'employed_by_security_or_financial_regulatory' &&
                      props.modal_config.object_to_edit.employed_by_security_or_financial_regulatory === 'Yes'
                    "
                    class="mt-4 flex flex-col gap-3"
                  >
                    <div class="flex flex-col md:flex-row gap-3">
                      <div class="flex-1">
                        <BaseInput
                          label="Employer Entity Name"
                          field_name="securities_employer_name"
                          field_type="string"
                          v-model="dataStore.contact.securities_employer_name"
                          :error_message="showEmployerErrors && !dataStore.contact.securities_employer_name?.trim() ? 'This field is required.' : ''"
                        />
                      </div>
                      <div class="flex-1">
                        <BaseInput
                          label="Employer Address"
                          field_name="securities_employer_address"
                          field_type="string"
                          v-model="dataStore.contact.securities_employer_address"
                          :error_message="showEmployerErrors && !dataStore.contact.securities_employer_address?.trim() ? 'This field is required.' : ''"
                        />
                      </div>
                    </div>
                    <div>
                      <div
                        v-if="securitiesEmployerDoc"
                        class="flex items-center gap-2 px-3 py-2 rounded-xl bg-container-bg border border-outline-dividers w-fit"
                      >
                        <Icon name="FileText" class="h-4 w-4 shrink-0 text-strong" />
                        <span class="text-sm font-inter text-strongest truncate max-w-56">{{
                          securitiesEmployerDoc.file_name
                        }}</span>
                        <button type="button" @click="removeSRFile" class="ml-1 flex items-center">
                          <Icon name="X" class="h-4 w-4 text-strong hover:text-strongest" />
                        </button>
                      </div>
                      <div
                        v-else-if="isUploadingDoc"
                        class="flex items-center gap-2 px-3 py-2 text-sm font-inter text-strong"
                      >
                        <span>Uploading...</span>
                      </div>
                      <label
                        v-else
                        class="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-outline-dividers bg-container-bg text-sm font-inter font-medium text-strongest hover:bg-radio-checkbox-selector transition-colors"
                      >
                        <Icon name="Paperclip" class="h-4 w-4" />
                        <span>Attach Document</span>
                        <input type="file" class="hidden" @change="handleSRFileChange" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </TabView>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="flex justify-end gap-2">
        <Button @click="handleConfirmClose()" variant="paper-primary" size="sm" class="">
          <template #label>Cancel</template>
        </Button>
        <Button @click="handleConfirm()" variant="primary-grey" size="sm" class="">
          <template #label>Confirm</template>
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
//IMPORTS
import { computed, ref, watch } from "vue";
import { Modal, TabView, Form, Icon, Button, BaseInput } from "@/components";
import { useCrudStore, useSchemaStore, useDataStore, useUIStore, usePurchaseFlowStore } from "@/stores";
import _ from "lodash";
import DiscardChangesModal from "./DiscardChangesModal.vue";
//STORES
const crudStore = useCrudStore();
const dataStore = useDataStore();
const schemaStore = useSchemaStore();
const UIStore = useUIStore();
const purchaseFlowStore = usePurchaseFlowStore();

//EMITS & PROPS
const emit = defineEmits(["close-modal", "confirm"]);
const props = defineProps({
  open: { type: Boolean, required: true },
  modal_config: { type: Object, required: true },
});

//REFS
const validationErrors = ref({
  field_errors: {},
});
const activeProfileTab = ref("");
const showDiscardChangesModal = ref(false);
const isUploadingDoc = ref(false);
const showEmployerErrors = ref(false);
const showEmploymentErrors = ref(false);

const securitiesEmployerDoc = computed(
  () => dataStore.documents?.find((d) => d.file_category === "Securities Employer") ?? null
);

async function handleSRFileChange(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  isUploadingDoc.value = true;
  try {
    await purchaseFlowStore.uploadSecuritiesEmployerDoc(file);
  } finally {
    isUploadingDoc.value = false;
  }
}

async function removeSRFile() {
  await purchaseFlowStore.deleteSecuritiesEmployerDoc(securitiesEmployerDoc.value);
}

//WATCHERS
watch(
  () => props.modal_config.open,
  (is_open) => {
    if (is_open) {
      let found_tab = props.modal_config.tabs.find(
        (tab) => tab.label === props.modal_config.subtab
      );

      if (found_tab) {
        activeProfileTab.value = found_tab.slot_name;
      } else {
        activeProfileTab.value = props.modal_config.tabs[0].slot_name;
      }
    }
  },
  { immediate: true }
);

// watch object being edited, if it changes, reset validation errors
watch(
  () => [
    props.modal_config.object_to_edit?.current_liquid_net_worth,
    props.modal_config.object_to_edit?.current_net_worth,
    props.modal_config.object_to_edit?.current_income,
  ],
  () => {
    let current_liquid_net_worth = props.modal_config.object_to_edit?.current_liquid_net_worth;
    let current_net_worth = props.modal_config.object_to_edit?.current_net_worth;
    let current_income = props.modal_config.object_to_edit?.current_income;

    // if current income is greater than 10k and liquid networth is less than current networth and all 3 fields are at least greater than 1, set schemaStore.show_validation_errors to false as long as it was already true, to prevent showing errors on page load but still show them if user tries to save with invalid values
    if (
      current_liquid_net_worth < current_net_worth &&
      current_liquid_net_worth > 1 &&
      current_net_worth > 1 &&
      schemaStore.show_input_errors
    ) {
      schemaStore.show_input_errors = false;
    }
  },
  { immediate: true }
);

// COMPONENT LOGIC AND FUNCTIONS
function handleConfirmClose() {
  let values_changed = checkForChanges();
  let id_info_changed = checkForChanges("id_info");
  if (!values_changed && !id_info_changed) {
    emit("close-modal");
    return;
  }

  // show discard changes modal
  showDiscardChangesModal.value = true;
}

async function handleConfirm() {
  try {
    let values_changed = checkForChanges();
    let object = props.modal_config.object_to_edit;

    const empStatus = object.employment_status;
    const hasConditionalFields =
      empStatus === "Employed" ||
      empStatus === "Self-Employed" ||
      object.employed_by_security_or_financial_regulatory === "Yes";

    if (!values_changed && !hasConditionalFields) {
      emit("close-modal");
      return;
    }
    if (empStatus === "Employed" || empStatus === "Self-Employed") {
      if (!dataStore.contact.occupation?.trim() || !dataStore.contact.employer?.trim()) {
        showEmploymentErrors.value = true;
        return;
      }
    }

    const securitiesEmployed = object.employed_by_security_or_financial_regulatory;
    if (securitiesEmployed === "Yes") {
      if (
        !dataStore.contact.securities_employer_name?.trim() ||
        !dataStore.contact.securities_employer_address?.trim()
      ) {
        showEmployerErrors.value = true;
        return;
      }
    }

    let _continue = schemaStore.validateFields(
      { suitability_info: object },
      props.modal_config.schema,
      null,
      true,
      "questionnaire"
    );

    validationErrors.value = _continue;

    if (_continue.proceed) {
      //update contact

      let query = [
        {
          field_name: "record_id",
          operator: "eq",
          value: dataStore.contact.record_id,
        },
      ];

      let update = {
        suitability_info: object,
      };

      if (empStatus === "Employed" || empStatus === "Self-Employed") {
        update.occupation = dataStore.contact.occupation;
        update.employer = dataStore.contact.employer;
      }

      if (securitiesEmployed === "Yes") {
        update.securities_employer_name = dataStore.contact.securities_employer_name;
        update.securities_employer_address = dataStore.contact.securities_employer_address;
      }

      update = schemaStore.addCreatedUpdatedFields(update, false, true);

      let res = await crudStore.update("contacts", query, update);

      if (res.status !== 200) {
        throw new Error("Failed to update contact");
      }

      await handleActivityLog(true, null);

      emit("confirm", {
        type: props.modal_config.type,
        record_id: null,
      });

      emit("close-modal");
    } else {
      let invalid_tab = _continue.invalid_groups[0]
        ? props.modal_config.tabs.find((tab) => tab.label === _continue.invalid_groups[0])
        : props.modal_config.tabs[0];

      activeProfileTab.value = invalid_tab.slot_name;
      await handleActivityLog(false, _continue);
      return;
    }
  } catch (e) {
    var alert = {
      type: "error",
      message: `Failed to update investor questionnaire.`,
    };
    UIStore.animateNotificationAlert(alert);
    emit("close-modal");
  }
}

function checkForChanges() {
  let original = props.modal_config.original_object;
  let current = props.modal_config.object_to_edit;
  let changed = false;

  // if original is null or empty, consider it changed
  if (!original || _.isEmpty(original)) {
    return true;
  }

  for (let key in original) {
    if (original[key] !== current[key]) {
      changed = true;
      break;
    }
  }

  return changed;
}

const capitalize = (value) => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

async function handleActivityLog(valid, validation_details) {
  let status = valid ? "Completed" : "Failed Validation";

  let log = {
    step: "Profile Page",
    substep: "Questionnaire",
    status: status,
    message: "Updating investor questionnaire",
    url: window.location.href,
    validation_details: valid ? null : JSON.stringify(validation_details),
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  await crudStore.insert("investoractivity", [log]);
}
</script>
<!-- 
Validation that would need added from profile page if we allow questionnaire section to be edited:
Question 1 - Regardless of investment (specifically S1 and Reg D) capital preservation is never acceptable
Question 3 - Only significant or speculative can be selected regardless of investment
Question 11 - Only yes should be selected
Question 12 - Only yes should be selected
Question 13 - This is dependent on the investment:

If the client is editing the questionnaire through the profile page, not through the investment creation flow, maybe we can add a flag. For instance, if the client has both and 11-year and a 7-year investment, and they try to edit the time horizon to 1-5 year, the flag could say "This is not suitable for the current investment(s) you have on file." Or it can be simplified, let me know your thoughts. 
Question 14 - Only yes should be selected -->
